import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ArrowLeft, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface Estimate {
  id: string;
  estimate_number: string;
  title: string;
  status: string;
  created_at: string;
  total_amount?: number;
  expires_at?: string;
}

interface EstimateSelectorProps {
  customerId: string;
}

const EstimateSelector: React.FC<EstimateSelectorProps> = ({ customerId }) => {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<{ name: string; last_name: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomerAndEstimates();
  }, [customerId]);

  const fetchCustomerAndEstimates = async () => {
    try {
      setLoading(true);
      
      // Fetch customer details
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('name, last_name')
        .eq('id', customerId)
        .single();

      if (customerError) {
        console.error('Error fetching customer:', customerError);
        toast.error('Failed to load customer details');
        return;
      }

      setCustomer(customerData);

      // Fetch approved estimates for this customer
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          id,
          estimate_number,
          title,
          status,
          created_at,
          expires_at
        `)
        .eq('customer_id', customerId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching estimates:', error);
        toast.error('Failed to load estimates');
        return;
      }

      // Calculate total amounts for each estimate
      const estimatesWithTotals = await Promise.all(
        (data || []).map(async (estimate) => {
          const { data: lineItems } = await supabase
            .from('estimate_line_items')
            .select('total_cost_snapshot, manual_override, manual_total_cost')
            .eq('estimate_id', estimate.id);

          const totalAmount = lineItems?.reduce((sum, item) => {
            const cost = item.manual_override && item.manual_total_cost 
              ? item.manual_total_cost 
              : item.total_cost_snapshot;
            return sum + (cost || 0);
          }, 0) || 0;

          return {
            ...estimate,
            total_amount: totalAmount
          };
        })
      );

      setEstimates(estimatesWithTotals);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEstimate = (estimateId: string) => {
    navigate(`/invoices/create?estimate_id=${estimateId}`);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Select Estimate for Invoice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <p>Loading estimates...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={handleGoBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">
          Create Invoice for {customer?.name} {customer?.last_name}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Select an Approved Estimate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Standalone invoice option */}
          <div className="mb-6">
            <Button
              variant="outline"
              className="w-full justify-start gap-2 h-auto py-3"
              onClick={() => navigate(`/invoices/create?mode=standalone&customer=${customerId}`)}
            >
              <Plus className="h-4 w-4" />
              <div className="text-left">
                <p className="font-medium">Create Without Estimate</p>
                <p className="text-xs text-muted-foreground">Create a standalone invoice with manual line items</p>
              </div>
            </Button>
          </div>

          {estimates.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">
                Or choose an approved estimate to create an invoice from:
              </p>
              {estimates.map((estimate) => (
                <div
                  key={estimate.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => handleSelectEstimate(estimate.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{estimate.estimate_number}</span>
                      <Badge variant="default">
                        {estimate.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{estimate.title}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Created: {format(new Date(estimate.created_at), 'dd MMMM yyyy')}</span>
                      {estimate.expires_at && (
                        <span>Expires: {format(new Date(estimate.expires_at), 'dd MMMM yyyy')}</span>
                      )}
                      {estimate.total_amount !== undefined && (
                        <span className="font-medium text-foreground">
                          {formatCurrency(estimate.total_amount)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Select
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>No approved estimates found for this customer</p>
              <p className="text-sm mt-2">
                You can still create a standalone invoice with manual line items.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EstimateSelector;